import { useEffect, useRef, useState, type FormEvent, type ReactNode } from 'react'
import { useParams, Link } from 'react-router-dom'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import { ArrowLeft, Plug } from 'lucide-react'
import { api, apiErrorMessage } from '../lib/api'
import { useAuthStore } from '../stores/auth'
import type { ClientConfigDto, ClientDto, EaDocsTestResult, TestConnectionResult } from '@emag/shared'
import {
  COUNTRY_LABEL,
  INVOICING_MODES,
  INVOICING_MODE_LABEL,
  type ClientMarketplaceDto,
  type Country,
} from '@emag/shared'

function Card({ title, children }: { title: string; children: ReactNode }) {
  return (
    <div className="rounded-xl border border-b4l-border bg-white p-5">
      <h2 className="mb-4 text-[14px] font-semibold text-b4l-text">{title}</h2>
      {children}
    </div>
  )
}

function Field({
  label,
  value,
  onChange,
  type = 'text',
  placeholder,
  hint,
}: {
  label: string
  value: string
  onChange: (v: string) => void
  type?: string
  placeholder?: string
  hint?: string
}) {
  return (
    <div>
      <label className="mb-1 block text-[12px] text-b4l-text-muted">{label}</label>
      <input
        type={type}
        value={value}
        placeholder={placeholder}
        onChange={(e) => onChange(e.target.value)}
        // Nimic de pe pagina asta nu e un login: fara asta, Chrome completeaza datele de
        // logare in platforma in „API user” + „API parola” (si le salveaza ca credentiale eMAG).
        autoComplete={type === 'password' ? 'new-password' : 'off'}
        data-lpignore="true"
        className="w-full rounded-md border border-b4l-border px-3 py-1.5 text-[13px] outline-none focus:border-b4l-green"
      />
      {hint && <p className="mt-1 text-[11px] leading-snug text-b4l-text-dim">{hint}</p>}
    </div>
  )
}

const btn =
  'rounded-md bg-b4l-green px-4 py-1.5 text-[13px] font-medium text-white hover:bg-b4l-green-dark disabled:opacity-60'

export function ClientDetailPage() {
  const { id } = useParams()
  const clientId = Number(id)
  const qc = useQueryClient()
  const isAdmin = useAuthStore((s) => s.user?.role === 'admin')

  const { data: client } = useQuery({
    queryKey: ['client', clientId],
    queryFn: async () => (await api.get<{ client: ClientDto }>(`/clients/${clientId}`)).data.client,
  })
  const { data: config } = useQuery({
    queryKey: ['client-config', clientId],
    queryFn: async () => (await api.get<{ config: ClientConfigDto }>(`/clients/${clientId}/config`)).data.config,
  })

  // ── General ──
  const [name, setName] = useState('')
  const [cif, setCif] = useState('')
  const [active, setActive] = useState(true)
  const [invoicingMode, setInvoicingMode] = useState<string>('emag')
  const [returnGraceDays, setReturnGraceDays] = useState('30')
  useEffect(() => {
    if (client) {
      setName(client.name)
      setCif(client.cif)
      setActive(client.active)
      setInvoicingMode(client.invoicingMode)
      setReturnGraceDays(String(client.returnGraceDays))
    }
  }, [client])

  // -- Tari --
  const { data: marketplaces } = useQuery({
    queryKey: ['client-marketplaces', clientId],
    queryFn: async () =>
      (await api.get<{ marketplaces: ClientMarketplaceDto[] }>(`/clients/${clientId}/marketplaces`)).data
        .marketplaces,
  })
  type RandPiata = {
    enabled: boolean
    transit: string
    receivable: string
    supplierName: string
    supplierVat: string
    expense: string
    ads: string
    advance: string
  }
  const [mkt, setMkt] = useState<Record<string, RandPiata>>({})
  useEffect(() => {
    if (marketplaces) {
      setMkt(
        Object.fromEntries(
          marketplaces.map((m) => [
            m.country,
            {
              enabled: m.enabled,
              transit: m.transitAccount ?? '',
              receivable: m.receivableAccount ?? '',
              supplierName: m.supplierName ?? '',
              supplierVat: m.supplierVat ?? '',
              expense: m.expenseAccount ?? '',
              ads: m.adsExpenseAccount ?? '',
              advance: m.advanceAccount ?? '',
            },
          ]),
        ),
      )
    }
  }, [marketplaces])

  async function saveMarketplace(country: Country) {
    const row = mkt[country]
    if (!row) return
    try {
      await api.put(`/clients/${clientId}/marketplaces/${country}`, {
        enabled: row.enabled,
        transitAccount: row.transit,
        receivableAccount: row.receivable,
        supplierName: row.supplierName,
        supplierVat: row.supplierVat,
        expenseAccount: row.expense,
        adsExpenseAccount: row.ads,
        advanceAccount: row.advance,
      })
      toast.success(`${COUNTRY_LABEL[country]} - salvat`)
      void qc.invalidateQueries({ queryKey: ['client-marketplaces', clientId] })
    } catch (err) {
      toast.error(apiErrorMessage(err))
    }
  }

  // ── eMAG ──
  const [emagApiUser, setEmagApiUser] = useState('')
  const [emagApiPass, setEmagApiPass] = useState('')
  const [portalUser, setPortalUser] = useState('')
  const [portalPass, setPortalPass] = useState('')
  // ── EA ──
  const [eaBase, setEaBase] = useState('')
  const [eaInvDocsUrl, setEaInvDocsUrl] = useState('')
  const [eaTestOrder, setEaTestOrder] = useState('')
  const [eaTesting, setEaTesting] = useState(false)
  const [eaTest, setEaTest] = useState<EaDocsTestResult | null>(null)
  const [danteName, setDanteName] = useState('')
  const [danteCif, setDanteCif] = useState('')
  const [bankAccount, setBankAccount] = useState('')
  const [transitAccount, setTransitAccount] = useState('')
  const [receivableAccount, setReceivableAccount] = useState('')
  // Campurile se completeaza din config O SINGURA DATA per client. Altfel fiecare refetch
  // (ex. revenirea in tab) suprascrie ce tocmai ai tastat si campul pare needitabil.
  const loadedFor = useRef<number | null>(null)
  useEffect(() => {
    if (!config || loadedFor.current === clientId) return
    loadedFor.current = clientId
    if (config?.emag) {
      setEmagApiUser(config.emag.emagApiUser ?? '')
      setPortalUser(config.emag.portalUser ?? '')
    }
    if (config?.ea) {
      setEaBase(config.ea.eaApiBaseUrl ?? '')
      setDanteName(config.ea.danteName)
      setDanteCif(config.ea.danteCif)
      setBankAccount(config.ea.bankAccount ?? '')
      setTransitAccount(config.ea.transitAccount)
      setReceivableAccount(config.ea.salesReceivableAccount)
    }
  }, [config])

  const [testing, setTesting] = useState(false)

  async function saveGeneral(e: FormEvent) {
    e.preventDefault()
    try {
      const zile = Number(returnGraceDays)
      if (!Number.isInteger(zile) || zile < 1 || zile > 365) {
        toast.error('Pragul pentru retururi trebuie să fie un număr de zile între 1 și 365.')
        return
      }
      await api.patch(`/clients/${clientId}`, { name, cif, active, invoicingMode, returnGraceDays: zile })
      toast.success('Date generale salvate')
      void qc.invalidateQueries({ queryKey: ['client', clientId] })
      void qc.invalidateQueries({ queryKey: ['clients'] })
    } catch (err) {
      toast.error(apiErrorMessage(err))
    }
  }

  async function saveEmag(e: FormEvent) {
    e.preventDefault()
    const payload: Record<string, string> = {}
    if (emagApiUser) payload.emagApiUser = emagApiUser
    if (emagApiPass) payload.emagApiPass = emagApiPass
    if (portalUser) payload.portalUser = portalUser
    if (portalPass) payload.portalPass = portalPass
    try {
      await api.put(`/clients/${clientId}/emag-config`, payload)
      toast.success('Config eMAG salvat')
      setEmagApiPass('')
      setPortalPass('')
      void qc.invalidateQueries({ queryKey: ['client-config', clientId] })
      void qc.invalidateQueries({ queryKey: ['clients'] })
    } catch (err) {
      toast.error(apiErrorMessage(err))
    }
  }

  async function saveEa(e: FormEvent) {
    e.preventDefault()
    try {
      await api.put(`/clients/${clientId}/ea-config`, {
        eaApiBaseUrl: eaBase || undefined,
        eaInvDocsUrl: eaInvDocsUrl.trim() || undefined,
        danteName,
        danteCif,
        bankAccount: bankAccount || undefined,
        transitAccount,
        salesReceivableAccount: receivableAccount,
      })
      toast.success('Config ExpertAccounts salvat')
      setEaInvDocsUrl('')
      void qc.invalidateQueries({ queryKey: ['client-config', clientId] })
      void qc.invalidateQueries({ queryKey: ['clients'] })
    } catch (err) {
      toast.error(apiErrorMessage(err))
    }
  }

  async function testEaDocs() {
    setEaTesting(true)
    setEaTest(null)
    try {
      const res = await api.post<{ result: EaDocsTestResult }>(`/clients/${clientId}/test-ea-docs`, {
        order: eaTestOrder.trim(),
      })
      setEaTest(res.data.result)
      if (res.data.result.ok) toast.success(res.data.result.message)
      else toast.error(res.data.result.message, { duration: 8000 })
    } catch (err) {
      toast.error(apiErrorMessage(err))
    } finally {
      setEaTesting(false)
    }
  }

  async function testEmag() {
    setTesting(true)
    try {
      // Trimitem ce e TASTAT in formular, nu doar ce e salvat: asa poti incerca alt cont
      // fara sa-l suprascrii pe cel care merge. Gol = se folosesc credentialele salvate.
      const res = await api.post<{ result: TestConnectionResult }>(`/clients/${clientId}/test-emag`, {
        ...(emagApiUser.trim() ? { user: emagApiUser.trim() } : {}),
        ...(emagApiPass.trim() ? { pass: emagApiPass.trim() } : {}),
      })
      if (res.data.result.ok) toast.success(res.data.result.message)
      else toast.error(res.data.result.message, { duration: 8000 })
    } catch (err) {
      toast.error(apiErrorMessage(err))
    } finally {
      setTesting(false)
    }
  }

  return (
    <div className="mx-auto max-w-3xl">
      <Link to="/clienti" className="mb-3 inline-flex items-center gap-1 text-[13px] text-b4l-text-muted hover:text-b4l-text">
        <ArrowLeft size={15} /> Înapoi la clienți
      </Link>
      <h1 className="mb-5 text-lg font-semibold text-b4l-text">{client?.name ?? 'Client'}</h1>

      <div className="space-y-4">
        <Card title="General">
          <form onSubmit={saveGeneral} className="space-y-3">
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              <Field label="Nume" value={name} onChange={setName} />
              <Field label="CIF" value={cif} onChange={setCif} />
            </div>
            <div>
              <label className="mb-1 block text-[12px] text-b4l-text-muted">Sursa facturilor de vanzare</label>
              <select
                value={invoicingMode}
                onChange={(e) => setInvoicingMode(e.target.value)}
                className="w-full rounded-md border border-b4l-border px-3 py-1.5 text-[13px] outline-none focus:border-b4l-green"
              >
                {INVOICING_MODES.map((m) => (
                  <option key={m} value={m}>
                    {INVOICING_MODE_LABEL[m]}
                  </option>
                ))}
              </select>
              <p className="mt-1 text-[11px] leading-snug text-b4l-text-dim">
                Decide de unde ia generatorul facturile. Daca facturarea se face in ExpertAccounts, eMAG nu are
                facturi de returnat, iar sursa devine importul &bdquo;Datorii si creante&rdquo;.
              </p>
            </div>
            <Field
              label="Retururi: prag „produs neajuns” (zile)"
              type="number"
              value={returnGraceDays}
              onChange={setReturnGraceDays}
              hint="După câte zile de la ridicarea coletului un retur rambursat, dar nerecepționat, apare în Retururi ca „Produs neajuns — de reclamat la eMAG”. Același prag îl folosește Verificare → Rambursări."
            />
            <label className="flex items-center gap-2 text-[13px] text-b4l-text-muted">
              <input type="checkbox" checked={active} onChange={(e) => setActive(e.target.checked)} />
              Activ
            </label>
            {isAdmin && (
              <button type="submit" className={btn}>
                Salvează
              </button>
            )}
          </form>
        </Card>

        <Card title="Credențiale eMAG">
          <form onSubmit={saveEmag} className="space-y-3">
            <p className="text-[12px] leading-relaxed text-b4l-text-muted">
              Două seturi diferite de credențiale, niciunul nu e contul tău de login în platformă:{' '}
              <strong>API</strong> — contul tehnic folosit la sincronizarea comenzilor și facturilor;{' '}
              <strong>Portal</strong> — contul cu care se intră în Seller Center din browser.
            </p>
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              <Field
                label="API user"
                value={emagApiUser}
                onChange={setEmagApiUser}
                hint="Utilizatorul tehnic din Seller Center → Setări → Marketplace API. Contul trebuie să aibă drepturi de API activate."
              />
              <Field
                label="API parolă / token"
                type="password"
                value={emagApiPass}
                onChange={setEmagApiPass}
                placeholder={config?.emag?.hasEmagApiPass ? '•••••• (setată)' : 'nesetată'}
                hint="Parola sau token-ul contului de API — nu parola de Seller Center."
              />
              <Field
                label="Portal user (scraper)"
                value={portalUser}
                onChange={setPortalUser}
                hint="Contul de Seller Center, folosit doar de scraperul de deconturi. Scraperul nu e încă activ — poți lăsa gol."
              />
              <Field
                label="Portal parolă"
                type="password"
                value={portalPass}
                onChange={setPortalPass}
                placeholder={config?.emag?.hasPortalPass ? '•••••• (setată)' : 'nesetată'}
              />
            </div>
            {isAdmin && (
              <div className="flex items-center gap-2">
                <button type="submit" className={btn}>
                  Salvează
                </button>
                <button
                  type="button"
                  onClick={testEmag}
                  disabled={testing}
                  className="flex items-center gap-1.5 rounded-md border border-b4l-border px-3 py-1.5 text-[13px] text-b4l-text-muted hover:bg-b4l-bg-soft disabled:opacity-60"
                >
                  <Plug size={14} />
                  {testing ? 'Se testează…' : 'Test conexiune API'}
                </button>
              </div>
            )}
            <p className="text-[11px] leading-snug text-b4l-text-dim">
              Parolele nu se afișează niciodată — se stochează criptat (AES-256). Lasă gol ca să păstrezi valoarea curentă.
              <br />
              Dacă testul întoarce <em>„Invalid vendor ip"</em>, credențialele sunt corecte — eMAG cere ca adresa IP a
              serverului să fie pe lista albă (Seller Center → Contul meu → Profil → Detalii tehnice → Adrese IP).
              <br />
              Testul folosește ce e <strong>tastat acum</strong> în câmpuri, chiar dacă n-ai salvat — așa poți încerca
              alt cont fără să-l pierzi pe cel care merge.
            </p>
          </form>
        </Card>

        <Card title="Tari">
          <p className="mb-3 text-[12px] leading-relaxed text-b4l-text-muted">
            Tarile eMAG pe care vinde clientul. Conturile lasate goale se <strong>mostenesc</strong> din
            configurarea ExpertAccounts de mai jos - completeaza-le doar daca vrei analitice separate pe tara.
          </p>
          <div className="space-y-3">
            {(marketplaces ?? []).map((m) => {
              const row =
                mkt[m.country] ??
                ({
                  enabled: m.enabled,
                  transit: '',
                  receivable: '',
                  supplierName: '',
                  supplierVat: '',
                  expense: '',
                  ads: '',
                  advance: '',
                } as RandPiata)
              const upd = (patch: Partial<typeof row>) =>
                setMkt((prev) => ({ ...prev, [m.country]: { ...row, ...patch } }))
              return (
                <div key={m.country} className="rounded-md border border-b4l-border p-3">
                  <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
                    <label className="flex items-center gap-2 text-[13px] font-medium text-b4l-text">
                      <input
                        type="checkbox"
                        checked={row.enabled}
                        onChange={(e) => upd({ enabled: e.target.checked })}
                      />
                      {COUNTRY_LABEL[m.country]}
                      <span className="font-normal text-b4l-text-dim">
                        ({m.country} &middot; {m.currency})
                      </span>
                    </label>
                    <span className="text-[11px] text-b4l-text-dim">
                      {m.lastOrdersSyncAt
                        ? `ultima sincronizare: ${new Date(m.lastOrdersSyncAt).toLocaleString('ro-RO')}`
                        : 'nesincronizata'}
                    </span>
                  </div>
                  <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                    <Field
                      label="Cont tranzit"
                      value={row.transit}
                      onChange={(v) => upd({ transit: v })}
                      placeholder="mostenit"
                    />
                    <Field
                      label="Cont creante"
                      value={row.receivable}
                      onChange={(v) => upd({ receivable: v })}
                      placeholder="mostenit"
                    />
                  </div>

                  <p className="mb-1 mt-3 text-[11px] font-medium text-b4l-text-muted">
                    Furnizorul serviciilor eMAG pe piața asta
                  </p>
                  <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                    <Field
                      label="Nume partener (din ExpertAccounts)"
                      value={row.supplierName}
                      onChange={(v) => upd({ supplierName: v })}
                      placeholder={m.country === 'RO' ? 'moștenit din Dante' : 'nesetat'}
                      hint="Exact cum e scris în EA, nu cum apare pe factură — din el se fac analiticele de 401 și 4092. O literă în plus și importul nu se lipește."
                    />
                    <Field
                      label="Cod TVA furnizor"
                      value={row.supplierVat}
                      onChange={(v) => upd({ supplierVat: v })}
                      placeholder="ex. BG203187055"
                      hint="Folosit la D390, pentru achizițiile intracomunitare."
                    />
                    <Field
                      label="Cont cheltuieli servicii"
                      value={row.expense}
                      onChange={(v) => upd({ expense: v })}
                      placeholder={`628.eMag${m.country}`}
                      hint="Comision, livrare, transport."
                    />
                    <Field
                      label="Cont publicitate (eMAG Ads)"
                      value={row.ads}
                      onChange={(v) => upd({ ads: v })}
                      placeholder="6232"
                      hint="Separat de restul serviciilor."
                    />
                    <Field
                      label="Cont avansuri promovare"
                      value={row.advance}
                      onChange={(v) => upd({ advance: v })}
                      placeholder="4092.<furnizor>"
                      hint="Se stornează la anexa de decontare, când serviciul e prestat."
                    />
                  </div>
                  {isAdmin && (
                    <button type="button" onClick={() => void saveMarketplace(m.country)} className={`${btn} mt-3`}>
                      Salveaza
                    </button>
                  )}
                </div>
              )
            })}
          </div>
        </Card>

        <Card title="ExpertAccounts">
          <form onSubmit={saveEa} className="space-y-3">
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              <Field label="EA API base URL" value={eaBase} onChange={setEaBase} placeholder="https://erp.….expert-account.com" />
              <Field label="Cont tranzit (încasări)" value={transitAccount} onChange={setTransitAccount} />
              <Field
                label="Cont creante clienti"
                value={receivableAccount}
                onChange={setReceivableAccount}
                hint="Folosit cand tara nu are cont propriu. In modul ExpertAccounts contul vine din export."
              />
              <Field label="Partener Dante (nume)" value={danteName} onChange={setDanteName} />
              <Field label="CIF Dante" value={danteCif} onChange={setDanteCif} />
              <Field label="Cont bancă" value={bankAccount} onChange={setBankAccount} placeholder="5121.01" />
            </div>
            <Field
              label="Conexiune API facturi (Endpoint URL din ExpertAccounts)"
              type="password"
              value={eaInvDocsUrl}
              onChange={setEaInvDocsUrl}
              placeholder={config?.ea?.hasInvDocsUrl ? '•••••• (setată)' : 'nesetată'}
              hint="Din ExpertAccounts: Meniu → Configurari → Configurari sistem → fila „Configurare API” → secțiunea „Inbound API endpoints”, rândul conexiunii cu API module get_data, coloana „Endpoint URL”. V1 rămâne gol: sursa o trimite platforma. Conține cheile, deci se salvează criptat și nu se mai afișează. Lasă gol ca să păstrezi valoarea curentă."
            />
            {isAdmin && (
              <div className="flex flex-wrap items-center gap-2">
                <button type="submit" className={btn}>
                  Salvează
                </button>
                <input
                  value={eaTestOrder}
                  onChange={(e) => setEaTestOrder(e.target.value)}
                  placeholder="nr. comandă eMAG"
                  autoComplete="off"
                  className="w-40 rounded-md border border-b4l-border px-3 py-1.5 text-[13px] outline-none focus:border-b4l-green"
                />
                <button
                  type="button"
                  onClick={testEaDocs}
                  disabled={eaTesting || !eaTestOrder.trim() || !config?.ea?.hasInvDocsUrl}
                  className="flex items-center gap-1.5 rounded-md border border-b4l-border px-3 py-1.5 text-[13px] text-b4l-text-muted hover:bg-b4l-bg-soft disabled:opacity-60"
                >
                  <Plug size={14} />
                  {eaTesting ? 'Se testează…' : 'Test conexiune facturi'}
                </button>
              </div>
            )}
            {eaTest && (
              <div className="space-y-2 rounded-md border border-b4l-border p-3 text-[12px]">
                <p className={eaTest.ok ? 'text-b4l-green' : 'text-red-600'}>{eaTest.message}</p>
                {eaTest.docs.length > 0 && (
                  <table className="w-full text-left">
                    <thead className="text-b4l-text-muted">
                      <tr>
                        <th>Tip</th>
                        <th>Număr</th>
                        <th>Data</th>
                        <th className="text-right">Linii</th>
                        <th className="text-right">Total 4111</th>
                      </tr>
                    </thead>
                    <tbody>
                      {eaTest.docs.map((d) => (
                        <tr key={`${d.doct}|${d.docn}`}>
                          <td>{d.doct}</td>
                          <td>{d.docn}</td>
                          <td>{d.docd}</td>
                          <td className="text-right">{d.lines}</td>
                          <td className="text-right">{d.total.toFixed(2)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
                {eaTest.columns.length > 0 && (
                  <p className="break-words text-b4l-text-dim">Coloane primite: {eaTest.columns.join(', ')}</p>
                )}
                {eaTest.sample && (
                  <pre className="max-h-60 overflow-auto whitespace-pre-wrap text-[11px] text-b4l-text-dim">
                    {JSON.stringify(eaTest.sample, null, 1)}
                  </pre>
                )}
              </div>
            )}
          </form>
        </Card>
      </div>
    </div>
  )
}
